import { useState } from "react";
import { saveCosto } from "../../../api";
import { MobileSheet } from "../../social/mobile/MobileChrome";
import type { CostoPrefill } from "../CostoForm";
import { payloadCosto } from "../costosData";
import { CATEGORIAS_MANUALES, fmtClp, mesLargo, type Moneda } from "../costosFormat";
import { useMontoCosto } from "../useMontoCosto";

interface Props {
  mes: string;
  prefill: CostoPrefill;
  theme: "light" | "dark";
  onSaved: (mensaje: string) => void;
  onClose: () => void;
}

const MONEDAS: Moneda[] = ["CLP", "USD"];

/** Carga de un costo en el teléfono: categoría en fichas, monto grande en CLP o USD y guardado fijo al pie de la hoja. */
export function CostoFormMobile({ mes, prefill, theme, onSaved, onClose }: Props) {
  const [categoria, setCategoria] = useState(prefill.categoria ?? CATEGORIAS_MANUALES[0]);
  const [concepto, setConcepto] = useState(prefill.concepto ?? "");
  const monto = useMontoCosto(prefill.monto);
  const [periodico, setPeriodico] = useState(prefill.periodico ?? false);
  const [nota, setNota] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const admitePeriodico = categoria !== "GCP";
  const puedeTerminar = !!prefill.periodico && !!prefill.mesOrigen && prefill.mesOrigen < mes;
  const valido = concepto.trim().length > 0 && !isNaN(monto.montoClp) && monto.montoClp >= 0;

  const guardar = async (terminar = false) => {
    if (!valido || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      await saveCosto(payloadCosto({ mes, prefill, categoria, concepto, montoClp: monto.montoClp, nota: monto.notaFinal(nota), periodico: admitePeriodico && periodico, terminar }));
      onSaved(terminar ? "Costo terminado" : prefill.id ? "Costo actualizado" : "Costo agregado");
    } catch (err: any) {
      setError(err?.message || "No se pudo guardar el costo.");
      setGuardando(false);
    }
  };

  return (
    <MobileSheet
      open
      theme={theme}
      onClose={onClose}
      title={`${prefill.id ? "Editar costo" : "Nuevo costo"} · ${mesLargo(mes)}`}
      footer={
        <button type="submit" form="cm-form" className="sbx-m-action" disabled={!valido || guardando}>
          {guardando ? "Guardando…" : prefill.id ? "Actualizar" : "Guardar"}
        </button>
      }
    >
      <form id="cm-form" className="cm-form" onSubmit={(e) => { e.preventDefault(); void guardar(); }}>
        <div className="cm-field">
          <span className="cm-label">Categoría</span>
          <div className="cm-chips" role="radiogroup" aria-label="Categoría">
            {CATEGORIAS_MANUALES.map((c) => (
              <button key={c} type="button" role="radio" aria-checked={c === categoria} className={`cm-chip${c === categoria ? " is-on" : ""}`} ref={(el) => { if (el && c === categoria) el.scrollIntoView({ inline: "center", block: "nearest" }); }} onClick={() => setCategoria(c)}>{c}</button>
            ))}
          </div>
        </div>
        <label className="cm-field">
          <span className="cm-label">Concepto</span>
          <input className="cm-input" type="text" enterKeyHint="next" autoComplete="off" placeholder="Ej. Vultr Santiago" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
        </label>
        <div className="cm-field">
          <span className="cm-label">Monto{admitePeriodico && periodico ? " de cada mes" : " del mes"}</span>
          <div className="cm-chips cm-chips-fit" role="radiogroup" aria-label="Moneda">
            {MONEDAS.map((m) => (
              <button key={m} type="button" role="radio" aria-checked={monto.moneda === m} className={`cm-chip${monto.moneda === m ? " is-on" : ""}`} onClick={() => monto.cambiarMoneda(m)}>{m}</button>
            ))}
          </div>
          <span className="cm-amount">
            <span aria-hidden="true">{monto.esUsd ? "US$" : "$"}</span>
            <input className="cm-input cm-input-big" type="text" inputMode={monto.esUsd ? "decimal" : "numeric"} enterKeyHint="done" autoComplete="off" placeholder="0" aria-label="Monto" value={monto.texto} onChange={(e) => monto.escribir(e.target.value)} />
          </span>
          {monto.esUsd && (
            <span className={monto.dolarFalla ? "cm-error" : "cm-preview"}>
              {monto.dolarFalla ? "No se pudo obtener el dólar del día; ingresa el monto en CLP."
                : !monto.dolar ? "Buscando el dólar del día…"
                : isNaN(monto.montoClp) ? `Dólar del ${monto.dolar.fecha}: $${String(monto.dolar.valor).replace(".", ",")}`
                : `≈ ${fmtClp(monto.montoClp)} · ${monto.detalleUsd}`}
            </span>
          )}
        </div>
        {admitePeriodico && (
          <div className="cm-field">
            <span className="cm-label">Periodicidad</span>
            <div className="cm-chips cm-chips-fit" role="radiogroup" aria-label="Periodicidad">
              <button type="button" role="radio" aria-checked={!periodico} className={`cm-chip${!periodico ? " is-on" : ""}`} onClick={() => setPeriodico(false)}>Una sola vez</button>
              <button type="button" role="radio" aria-checked={periodico} className={`cm-chip${periodico ? " is-on" : ""}`} onClick={() => setPeriodico(true)}>Recurrente</button>
            </div>
            {periodico && <span className="cm-preview">Cuenta todos los meses desde {mesLargo(prefill.mesOrigen ?? mes)}{prefill.id ? "; editar cambia todos los meses." : "."}</span>}
          </div>
        )}
        <label className="cm-field">
          <span className="cm-label">Nota (opcional)</span>
          <input className="cm-input" type="text" enterKeyHint="done" value={nota} onChange={(e) => setNota(e.target.value)} />
        </label>
        {categoria === "GCP" && <p className="cm-hint">Un costo de GCP cargado a mano reemplaza a la exportación solo en meses que la exportación no cubre completos.</p>}
        {puedeTerminar && (
          <button type="button" className="cm-chip cm-terminar" disabled={!valido || guardando} onClick={() => void guardar(true)}>Terminar desde {mesLargo(mes)}</button>
        )}
        {error && <p className="cm-error" role="alert">{error}</p>}
      </form>
    </MobileSheet>
  );
}
